import type { FC } from "react";
import React, { useState } from "react";
import {
  useTranslation,
  useBrand,
  useTracking,
} from "@edreamsodigeo/frontend-core";
import { Body, Box, Flex, Glyph } from "@edreamsodigeo/prisma-design-system";

import { useFilterOfferByType } from "~/clientState/hooks/useFilterOfferByType";
import useGetSubscriptionOffers from "~/clientState/hooks/useGetSubscriptionOffers";
import {
  SubscriptionOfferPeriod,
  SubscriptionOfferType,
} from "~/generated/graphql";
import PrimeLogo from "~/public/PrimeLogo";
import { freeTrialDays } from "~/tools/subscriptionUtils";
import { TRACKING_DATA } from "./config/tracking";
import type { FreeTrialBannerProps } from "./contract";
import { BenefitsModal } from "~/components/common/BenefitsModal";

const FreeTrialBanner: FC<FreeTrialBannerProps> = ({
  nonDiscountedPrice,
  discountedPrice,
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const offerPeriod = SubscriptionOfferPeriod.FreeTrial;
  const isGovoyage = useBrand() === "GO";
  const { offers } = useGetSubscriptionOffers({
    offerPeriod,
  });
  const basicOffer = useFilterOfferByType({
    offersList: offers,
    subscriptionOfferType: SubscriptionOfferType.Basic,
  });
  const title = t("flightSummary.equalProminence.freeTrialBanner.text", {
    freeTrialDays: freeTrialDays(basicOffer?.duration),
  });

  const savings = {
    amount: nonDiscountedPrice.amount - discountedPrice.amount,
    currency: nonDiscountedPrice.currency,
  };

  const onClickBanner = (): void => {
    trackEvent(TRACKING_DATA.onClickBanner);
    setIsModalOpen(!isModalOpen);
  };

  const stylesByMarket = isGovoyage
    ? ({
        textPaddingTop: "2x_small",
        textLineHeight: "20px",
        iconPaddingTop: "tiny",
      } as const)
    : ({
        textPaddingTop: "0",
        textLineHeight: null,
        iconPaddingTop: "0",
      } as const);

  return (
    <>
      <BenefitsModal
        offer={basicOffer}
        isOpen={isModalOpen}
        setOpen={setIsModalOpen}
        savings={savings}
      />
      <Box
        sx={{ cursor: "pointer" }}
        backgroundColor={"primary.90"}
        onClick={onClickBanner}
        data-testid={"freeTrialBanner"}
      >
        <Flex
          justifyContent={"center"}
          alignItems={"center"}
          gap={"1x_small"}
          px={"2x_small"}
          py={"2x_small"}
        >
          <PrimeLogo size={"xs"} negative={true} type={"short"} />
          <Box pt={stylesByMarket.textPaddingTop}>
            <Body
              type={"small"}
              color={"neutral.0"}
              sx={{ lineHeight: stylesByMarket.textLineHeight }}
            >
              {title}
            </Body>
          </Box>
          <Flex alignItems={"center"} pt={stylesByMarket.iconPaddingTop}>
            <Glyph
              name={"InfoCircle"}
              category={"Alerts"}
              color={"neutral.0"}
              size={"small"}
            />
          </Flex>
        </Flex>
      </Box>
    </>
  );
};
export default FreeTrialBanner;
